import React, { useState, useEffect } from 'react';
import BadgeAvatar from '../components/BadgeAvatar';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';

const Admin = ({ user, logout }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [comments, setComments] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [editingTest, setEditingTest] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      // Try to fetch admin stats to verify access
      const response = await api.get('/admin/stats');
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
        window.location.href = '/dashboard';
      } else {
        console.error('Error checking admin access:', error);
        toast.error('Failed to load admin panel');
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const fetchCaseStudies = async () => {
    try {
      const response = await api.get('/case-studies');
      setCaseStudies(response.data);
    } catch (error) {
      console.error('Error fetching case studies:', error);
      toast.error('Failed to load case studies');
    }
  };

  const fetchComments = async () => {
    try {
      const response = await api.get('/admin/comments');
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    }
  };

  const fetchTests = async () => {
    try {
      const response = await api.get('/admin/tests/all');
      setTests(response.data);
    } catch (error) {
      console.error('Error fetching tests:', error);
      toast.error('Failed to load tests');
    }
  };

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'cases') fetchCaseStudies();
    if (activeTab === 'tests') fetchTests();
    if (activeTab === 'comments') fetchComments();
  }, [activeTab]);

  const toggleAdminStatus = async (userId) => {
    try {
      const response = await api.put(`/admin/users/${userId}/toggle-admin`);
      toast.success(`Admin status updated`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling admin:', error);
      toast.error('Failed to update admin status');
    }
  };

  const toggleModeratorStatus = async (userId) => {
    try {
      const response = await api.put(`/admin/users/${userId}/toggle-moderator`);
      toast.success(`Moderator status updated`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling moderator:', error);
      toast.error('Failed to update moderator status');
    }
  };

  const toggleStudyMasterStatus = async (userId) => {
    try {
      const response = await api.put(`/admin/users/${userId}/toggle-study-master`);
      toast.success(`Study Master status updated`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling study master:', error);
      toast.error('Failed to update study master status');
    }
  };

  const toggleSuspendUser = async (userId) => {
    try {
      const response = await api.put(`/admin/users/${userId}/suspend`);
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      console.error('Error suspending user:', error);
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE user "${userName}"? This action cannot be undone.`)) return;
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await api.put(`/admin/users/${resetPasswordUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully. User must log in again.');
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const deleteCaseStudy = async (caseStudyId) => {
    if (!window.confirm('Are you sure you want to delete this case study?')) return;
    
    try {
      await api.delete(`/admin/case-studies/${caseStudyId}`);
      toast.success('Case study deleted');
      fetchCaseStudies();
    } catch (error) {
      console.error('Error deleting case study:', error);
      toast.error('Failed to delete case study');
    }
  };

  const toggleFeatured = async (caseStudyId) => {
    try {
      await api.put(`/admin/case-studies/${caseStudyId}/feature`);
      toast.success('Featured status updated');
      fetchCaseStudies();
    } catch (error) {
      console.error('Error updating featured:', error);
      toast.error('Failed to update featured status');
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await api.delete(`/admin/comments/${commentId}`);
      toast.success('Comment deleted');
      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const startEditComment = (comment) => {
    setEditingComment(comment.id);
    setEditCommentContent(comment.content);
  };

  const cancelEditComment = () => {
    setEditingComment(null);
    setEditCommentContent('');
  };

  const updateComment = async (commentId) => {
    if (!editCommentContent.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    try {
      await api.put(`/admin/comments/${commentId}`, {
        content: editCommentContent
      });
      toast.success('Comment updated');
      setEditingComment(null);
      setEditCommentContent('');
      fetchComments();
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
    }
  };

  const toggleFeaturedTest = async (testId) => {
    try {
      await api.put(`/admin/tests/${testId}/featured`);
      toast.success('Featured status updated');
      fetchTests();
    } catch (error) {
      console.error('Error toggling featured:', error);
      toast.error('Failed to update featured status');
    }
  };

  const updateTestTime = async (testId, newTime) => {
    try {
      await api.put(`/admin/tests/${testId}`, { time_limit: newTime });
      toast.success('Test time updated');
      setEditingTest(null);
      fetchTests();
    } catch (error) {
      console.error('Error updating test:', error);
      toast.error('Failed to update test time');
    }
  };

  const deleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this test?')) return;
    
    try {
      await api.delete(`/admin/tests/${testId}`);
      toast.success('Test deleted');
      fetchTests();
    } catch (error) {
      console.error('Error deleting test:', error);
      toast.error('Failed to delete test');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
        <div className='text-center'>
          <div className='inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600'></div>
          <p className='mt-4 text-teal-600 font-medium'>Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      {/* Header */}
      <header className='bg-white shadow-sm border-b border-gray-200'>
        <div className='container mx-auto px-4 py-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center space-x-4'>
              <Link to='/dashboard' className='flex items-center space-x-3'>
                <div className='w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center'>
                  <svg className='w-6 h-6 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' />
                  </svg>
                </div>
                <span className='text-xl font-bold text-gray-900' style={{ fontFamily: 'Playfair Display, serif' }}>Case Study Club</span>
              </Link>
              <Badge className='bg-gradient-to-r from-purple-500 to-pink-600 text-white border-0'>Admin</Badge>
            </div>
            <div className='flex items-center space-x-4'>
              <Link to='/dashboard'>
                <Button variant='outline' className='border-gray-300 text-gray-700 hover:bg-gray-100 rounded-full px-6'>
                  Back to Site
                </Button>
              </Link>
              <Link to={`/profile/${user.id}`}>
                <img
                  src={user.picture || 'https://via.placeholder.com/40'}
                  alt={user.name}
                  className='w-10 h-10 rounded-full border-2 border-teal-500 hover:border-teal-600 transition-all cursor-pointer'
                />
              </Link>
              <Button onClick={logout} variant='outline' className='border-gray-300 text-gray-700 hover:bg-gray-100 rounded-full px-6'>
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className='container mx-auto px-4 py-8 max-w-7xl'>
        <div className='bg-white rounded-3xl p-8 shadow-lg'>
          <div className='mb-8'>
            <h1 className='text-4xl font-bold text-gray-900 mb-2' style={{ fontFamily: 'Playfair Display, serif' }}>
              Admin Dashboard
            </h1>
            <p className='text-gray-600'>Manage your Case Study Club</p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className='w-full'>
            <TabsList className='grid w-full grid-cols-5 mb-8 bg-gray-100 p-1 rounded-xl'>
              <TabsTrigger value='dashboard' className='rounded-lg' data-testid='tab-dashboard'>
                Dashboard
              </TabsTrigger>
              <TabsTrigger value='users' className='rounded-lg' data-testid='tab-users'>
                Users
              </TabsTrigger>
              <TabsTrigger value='cases' className='rounded-lg' data-testid='tab-cases'>
                Case Studies
              </TabsTrigger>
              <TabsTrigger value='tests' className='rounded-lg' data-testid='tab-tests'>
                Tests
              </TabsTrigger>
              <TabsTrigger value='comments' className='rounded-lg' data-testid='tab-comments'>
                Comments
              </TabsTrigger>
            </TabsList>

            {/* Dashboard Tab */}
            <TabsContent value='dashboard'>
              <div className='space-y-6'>
                {/* Stats Cards */}
                <div className='grid grid-cols-2 md:grid-cols-5 gap-4'>
                  <div className='bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-200'>
                    <div className='text-3xl font-bold text-teal-600'>{stats?.total_users || 0}</div>
                    <div className='text-sm text-gray-600 mt-1'>Total Users</div>
                  </div>
                  <div className='bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200'>
                    <div className='text-3xl font-bold text-blue-600'>{stats?.total_case_studies || 0}</div>
                    <div className='text-sm text-gray-600 mt-1'>Case Studies</div>
                  </div>
                  <div className='bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200'>
                    <div className='text-3xl font-bold text-green-600'>{stats?.simple_cases || 0}</div>
                    <div className='text-sm text-gray-600 mt-1'>Simple Cases</div>
                  </div>
                  <div className='bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200'>
                    <div className='text-3xl font-bold text-orange-600'>{stats?.complex_cases || 0}</div>
                    <div className='text-sm text-gray-600 mt-1'>Complex Cases</div>
                  </div>
                  <div className='bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200'>
                    <div className='text-3xl font-bold text-purple-600'>{stats?.total_comments || 0}</div>
                    <div className='text-sm text-gray-600 mt-1'>Comments</div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className='grid md:grid-cols-2 gap-6 mt-8'>
                  <div className='bg-gray-50 rounded-xl p-6'>
                    <h3 className='text-lg font-bold text-gray-900 mb-4'>Recent Users</h3>
                    <div className='space-y-3'>
                      {stats?.recent_users?.slice(0, 5).map(u => (
                        <div key={u.id} className='flex items-center justify-between bg-white rounded-lg p-3'>
                          <div className='flex items-center space-x-3'>
                            <img src={u.picture || 'https://via.placeholder.com/32'} alt={u.name} className='w-8 h-8 rounded-full' />
                            <div>
                              <div className='text-sm font-medium text-gray-900'>{u.name}</div>
                              <div className='text-xs text-gray-500'>{u.email}</div>
                            </div>
                          </div>
                          {u.is_admin && <Badge className='bg-purple-100 text-purple-700 border-0 text-xs'>Admin</Badge>}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className='bg-gray-50 rounded-xl p-6'>
                    <h3 className='text-lg font-bold text-gray-900 mb-4'>Recent Case Studies</h3>
                    <div className='space-y-3'>
                      {stats?.recent_case_studies?.slice(0, 5).map(cs => (
                        <div key={cs.id} className='bg-white rounded-lg p-3'>
                          <div className='flex items-start justify-between'>
                            <div className='flex-1'>
                              <div className='text-sm font-medium text-gray-900 line-clamp-1'>{cs.title}</div>
                              <div className='text-xs text-gray-500 mt-1'>{cs.author_name}</div>
                            </div>
                            <Badge className={`ml-2 ${cs.difficulty === 'simple' ? 'difficulty-simple' : 'difficulty-complex'} border-0 text-xs`}>
                              {cs.difficulty}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value='users'>
              <div className='space-y-4'>
                <div className='flex items-center justify-between mb-6'>
                  <h3 className='text-xl font-bold text-gray-900'>User Management</h3>
                  <div className='text-sm text-gray-600'>Total: {users.length} users</div>
                </div>

                <div className='space-y-3'>
                  {users.map(u => (
                    <div key={u.id} className={`rounded-xl p-4 flex items-center justify-between transition-all ${u.is_suspended ? 'bg-red-50 border-2 border-red-300' : 'bg-gray-50 hover:bg-gray-100'}`}>
                      <div className='flex items-center space-x-4'>
                        <img src={u.picture || 'https://via.placeholder.com/40'} alt={u.name} className={`w-10 h-10 rounded-full ${u.is_suspended ? 'opacity-50 grayscale' : ''}`} />
                        <div>
                          <div className='flex items-center space-x-2'>
                            <div className={`font-medium ${u.is_suspended ? 'text-red-900' : 'text-gray-900'}`}>{u.name}</div>
                            {u.is_suspended && <Badge className='bg-red-500 text-white border-0'>🚫 SUSPENDED</Badge>}
                            {!u.is_suspended && u.is_admin && <Badge className='bg-purple-100 text-purple-700 border-0'>👑 Admin</Badge>}
                            {!u.is_suspended && u.is_moderator && !u.is_admin && <Badge className='bg-blue-100 text-blue-700 border-0'>🛡️ Moderator</Badge>}
                            {!u.is_suspended && u.is_study_master && !u.is_admin && !u.is_moderator && <Badge className='bg-green-100 text-green-700 border-0'>📚 Study Master</Badge>}
                          </div>
                          <div className='text-sm text-gray-600'>{u.email}</div>
                          <div className='text-xs text-gray-500 mt-1'>Joined: {formatDate(u.created_at)}</div>
                        </div>
                      </div>
                      <div className='flex items-center space-x-2'>
                        {!u.is_suspended && (
                          <>
                            <Button
                              onClick={() => toggleAdminStatus(u.id)}
                              variant='outline'
                              className='rounded-full text-xs'
                              data-testid={`toggle-admin-${u.id}`}
                            >
                              {u.is_admin ? 'Remove Admin' : 'Make Admin'}
                            </Button>
                            {!u.is_admin && (
                              <>
                                <Button
                                  onClick={() => toggleModeratorStatus(u.id)}
                                  variant='outline'
                                  className='rounded-full text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200'
                                  data-testid={`toggle-moderator-${u.id}`}
                                >
                                  {u.is_moderator ? 'Remove Mod' : 'Make Mod'}
                                </Button>
                                <Button
                                  onClick={() => toggleStudyMasterStatus(u.id)}
                                  variant='outline'
                                  className='rounded-full text-xs bg-green-50 hover:bg-green-100 text-green-700 border-green-200'
                                  data-testid={`toggle-study-master-${u.id}`}
                                >
                                  {u.is_study_master ? 'Remove SM' : 'Make SM'}
                                </Button>
                              </>
                            )}
                          </>
                        )}
                        {!u.is_admin && (
                          <>
                            <Button
                              onClick={() => toggleSuspendUser(u.id)}
                              variant='outline'
                              className={`rounded-full text-xs ${u.is_suspended ? 'bg-green-50 hover:bg-green-100 text-green-700 border-green-300' : 'bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-300'}`}
                              data-testid={`suspend-${u.id}`}
                            >
                              {u.is_suspended ? '✓ Unsuspend' : '⊘ Suspend'}
                            </Button>
                            {u.auth_provider === 'email' && (
                              <Button
                                onClick={() => setResetPasswordUser(u)}
                                variant='outline'
                                className='rounded-full text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300'
                              >
                                🔑 Reset Password
                              </Button>
                            )}
                            <Button
                              onClick={() => deleteUser(u.id, u.name)}
                              variant='destructive'
                              className='rounded-full text-xs bg-red-600 hover:bg-red-700 text-white'
                              data-testid={`delete-${u.id}`}
                            >
                              🗑 Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Case Studies Tab */}
            <TabsContent value='cases'>
              <div className='space-y-4'>
                <div className='flex items-center justify-between mb-6'>
                  <h3 className='text-xl font-bold text-gray-900'>Case Study Management</h3>
                  <div className='text-sm text-gray-600'>Total: {caseStudies.length} case studies</div>
                </div>

                <div className='space-y-3'>
                  {caseStudies.map(cs => (
                    <div key={cs.id} className='bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-all'>
                      <div className='flex items-start justify-between'>
                        <div className='flex-1'>
                          <div className='flex items-center space-x-2 mb-2'>
                            <h4 className='font-bold text-gray-900'>{cs.title}</h4>
                            <Badge className={`${cs.difficulty === 'simple' ? 'difficulty-simple' : 'difficulty-complex'} border-0 text-xs`}>
                              {cs.difficulty}
                            </Badge>
                            {cs.featured && <Badge className='bg-yellow-100 text-yellow-700 border-0 text-xs'>★ Featured</Badge>}
                          </div>
                          <p className='text-sm text-gray-600 mb-2'>{cs.description}</p>
                          <div className='flex items-center space-x-4 text-xs text-gray-500'>
                            <span>By {cs.author_name}</span>
                            <span>👁 {cs.view_count} views</span>
                            <span>👍 {cs.upvotes} upvotes</span>
                            <span>{formatDate(cs.created_at)}</span>
                          </div>
                        </div>
                        <div className='flex flex-col space-y-2 ml-4'>
                          <Button
                            onClick={() => toggleFeatured(cs.id)}
                            variant='outline'
                            size='sm'
                            className='rounded-full text-xs'
                          >
                            {cs.featured ? 'Unfeature' : 'Feature'}
                          </Button>
                          <Link to={`/case-study/${cs.id}`}>
                            <Button variant='outline' size='sm' className='rounded-full text-xs w-full'>
                              View
                            </Button>
                          </Link>
                          <Button
                            onClick={() => deleteCaseStudy(cs.id)}
                            variant='destructive'
                            size='sm'
                            className='rounded-full text-xs bg-red-500 hover:bg-red-600'
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Tests Tab */}
            <TabsContent value='tests'>
              <div className='space-y-6'>
                <div className='flex items-center justify-between mb-6'>
                  <h3 className='text-xl font-bold text-gray-900'>Test Management</h3>
                  <div className='flex gap-2'>
                    <button
                      onClick={() => {
                        const genForm = document.getElementById('test-gen-form');
                        if (genForm) genForm.style.display = genForm.style.display === 'none' ? 'block' : 'none';
                      }}
                      className='bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full px-6 py-2 text-sm hover:shadow-lg transition-all'
                    >
                      + Generate Test with AI
                    </button>
                  </div>
                </div>

                {/* AI Test Generation Form */}
                <div id='test-gen-form' style={{display: 'none'}} className='bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border-2 border-teal-200 mb-6'>
                  <h4 className='font-bold text-gray-900 mb-4'>AI Test Generator</h4>
                  <div className='space-y-4'>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>Difficulty</label>
                      <select id='test-difficulty' className='w-full border border-gray-300 rounded-lg p-2'>
                        <option value='simple'>Simple (Boards Prep)</option>
                        <option value='complex'>Complex (Clinic Ready)</option>
                      </select>
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>Time Limit (seconds)</label>
                      <input
                        id='test-time-limit'
                        type='number'
                        defaultValue='540'
                        min='60'
                        placeholder='e.g., 540 (9 minutes)'
                        className='w-full border border-gray-300 rounded-lg p-2'
                      />
                      <p className='text-xs text-gray-500 mt-1'>Recommended: 540 seconds (1.5 min per section)</p>
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>TCM Topic (Optional)</label>
                      <input
                        id='test-topic'
                        type='text'
                        placeholder='e.g., Liver Qi Stagnation, Kidney Deficiency'
                        className='w-full border border-gray-300 rounded-lg p-2'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>AI Model</label>
                      <select id='test-model' className='w-full border border-gray-300 rounded-lg p-2'>
                        <option value='gpt-4o-mini'>GPT-4o Mini (Fast)</option>
                        <option value='gpt-4o'>GPT-4o (Balanced)</option>
                        <option value='gpt-5'>GPT-5 (Most Advanced)</option>
                        <option value='claude-3-7-sonnet'>Claude 3.7 Sonnet</option>
                        <option value='claude-4-sonnet'>Claude 4 Sonnet</option>
                      </select>
                    </div>
                    <button
                      onClick={async () => {
                        const difficulty = document.getElementById('test-difficulty').value;
                        const topic = document.getElementById('test-topic').value;
                        const model = document.getElementById('test-model').value;
                        const timeLimit = parseInt(document.getElementById('test-time-limit').value) || 540;
                        
                        try {
                          toast('Generating test... This may take 30-60 seconds');
                          const response = await api.post('/admin/tests/generate', {
                            difficulty,
                            tcm_topic: topic,
                            model
                          });
                          
                          const output = `Test Generated!\\n\\nTime Limit: ${timeLimit} seconds\\n\\n${response.data.raw_response}\\n\\nNote: Copy this content and manually add to database with time_limit: ${timeLimit}`;
                          
                          const textarea = document.createElement('textarea');
                          textarea.value = output;
                          document.body.appendChild(textarea);
                          textarea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textarea);
                          
                          toast.success('Test generated and copied to clipboard!');
                          alert(output);
                        } catch (error) {
                          console.error('Error:', error);
                          toast.error('Failed to generate test');
                        }
                      }}
                      className='w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg px-6 py-3 font-medium hover:shadow-lg transition-all'
                    >
                      Generate Test
                    </button>
                  </div>
                </div>

                {/* Test List */}
                <div className='space-y-3'>
                  <div className='flex items-center justify-between mb-4'>
                    <h4 className='font-semibold text-gray-900'>Existing Tests</h4>
                    <div className='text-sm text-gray-600'>Total: {tests.length} tests</div>
                  </div>

                  {tests.length === 0 ? (
                    <div className='bg-gray-50 rounded-xl p-8 text-center'>
                      <p className='text-gray-600 mb-4'>No tests created yet</p>
                      <div className='flex gap-3 justify-center'>
                        <Link to='/test/simple'>
                          <button className='difficulty-simple border-0 px-6 py-2 rounded-full'>Preview Simple Test</button>
                        </Link>
                        <Link to='/test/complex'>
                          <button className='difficulty-complex border-0 px-6 py-2 rounded-full'>Preview Complex Test</button>
                        </Link>
                      </div>
                    </div>
                  ) : (
                    tests.map(test => (
                      <div key={test.id} className={`rounded-xl p-4 transition-all ${test.is_featured ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-400' : 'bg-gray-50 hover:bg-gray-100'}`}>
                        <div className='flex items-start justify-between'>
                          <div className='flex-1'>
                            <div className='flex items-center space-x-2 mb-2'>
                              <Badge className={`${test.difficulty === 'simple' ? 'difficulty-simple' : 'difficulty-complex'} border-0 text-xs`}>
                                {test.difficulty}
                              </Badge>
                              {test.is_featured && (
                                <Badge className='bg-yellow-500 text-white border-0 text-xs'>
                                  ⭐ FEATURED
                                </Badge>
                              )}
                              <span className='text-sm text-gray-600'>{formatDate(test.created_at)}</span>
                            </div>
                            <p className='text-sm text-gray-700 mb-2 line-clamp-2'>{test.case_scenario}</p>
                            <div className='flex items-center space-x-4 text-xs text-gray-500'>
                              <span>ID: {test.id.substring(0, 8)}...</span>
                              <span>Questions: {test.questions?.length || 6}</span>
                              {editingTest === test.id ? (
                                <div className='flex items-center space-x-2'>
                                  <input
                                    id={`time-input-${test.id}`}
                                    type='number'
                                    defaultValue={test.time_limit}
                                    min='60'
                                    className='w-20 border border-gray-300 rounded px-2 py-1 text-xs'
                                  />
                                  <span>seconds</span>
                                  <button
                                    onClick={() => {
                                      const newTime = parseInt(document.getElementById(`time-input-${test.id}`).value);
                                      updateTestTime(test.id, newTime);
                                    }}
                                    className='text-green-600 hover:text-green-700 font-medium'
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingTest(null)}
                                    className='text-gray-600 hover:text-gray-700'
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <span className='font-medium text-teal-600'>
                                  Time: {Math.floor(test.time_limit / 60)}:{(test.time_limit % 60).toString().padStart(2, '0')} ({test.time_limit}s)
                                </span>
                              )}
                            </div>
                          </div>
                          <div className='flex flex-col space-y-2 ml-4'>
                            {editingTest !== test.id && (
                              <>
                                <button
                                  onClick={() => toggleFeaturedTest(test.id)}
                                  className={`text-xs px-3 py-1 rounded-full font-semibold ${
                                    test.is_featured 
                                      ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
                                      : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                  }`}
                                >
                                  {test.is_featured ? '⭐ Featured' : 'Set Featured'}
                                </button>
                                <button
                                  onClick={() => setEditingTest(test.id)}
                                  className='text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full hover:bg-blue-200'
                                >
                                  Edit Time
                                </button>
                                <Link to={`/test/${test.difficulty}`}>
                                  <button className='text-xs bg-teal-100 text-teal-700 px-3 py-1 rounded-full hover:bg-teal-200 w-full'>
                                    Test
                                  </button>
                                </Link>
                                <button
                                  onClick={() => deleteTest(test.id)}
                                  className='text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full hover:bg-red-200'
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Comments Tab */}
            <TabsContent value='comments'>
              <div className='space-y-4'>
                <div className='flex items-center justify-between mb-6'>
                  <h3 className='text-xl font-bold text-gray-900'>Comment Moderation</h3>
                  <div className='text-sm text-gray-600'>Showing recent {comments.length} comments</div>
                </div>

                <div className='space-y-3'>
                  {comments.map(comment => (
                    <div key={comment.id} className='bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-all'>
                      <div className='flex items-start justify-between'>
                        <div className='flex-1'>
                          <div className='flex items-center space-x-2 mb-2'>
                            <img
                              src={comment.user_picture || 'https://via.placeholder.com/24'}
                              alt={comment.user_name}
                              className='w-6 h-6 rounded-full'
                            />
                            <span className='font-medium text-gray-900'>{comment.user_name}</span>
                            <span className='text-xs text-gray-500'>{formatDate(comment.created_at)}</span>
                          </div>
                          
                          {editingComment === comment.id ? (
                            <div className='mb-2'>
                              <textarea
                                value={editCommentContent}
                                onChange={(e) => setEditCommentContent(e.target.value)}
                                className='w-full p-2 border border-gray-300 rounded-lg text-sm min-h-[80px] focus:ring-2 focus:ring-teal-500 focus:border-teal-500'
                              />
                              <div className='flex space-x-2 mt-2'>
                                <Button
                                  onClick={() => updateComment(comment.id)}
                                  size='sm'
                                  className='rounded-full text-xs bg-teal-500 hover:bg-teal-600'
                                >
                                  Save
                                </Button>
                                <Button
                                  onClick={cancelEditComment}
                                  variant='outline'
                                  size='sm'
                                  className='rounded-full text-xs'
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <p className='text-sm text-gray-700 mb-2'>{comment.content}</p>
                          )}
                          
                          <div className='flex items-center space-x-3 text-xs text-gray-500'>
                            <span>Case Study ID: {comment.case_study_id}</span>
                            {comment.parent_id && <span>↳ Reply</span>}
                            <span>👍 {comment.upvotes}</span>
                          </div>
                        </div>
                        
                        {editingComment !== comment.id && (
                          <div className='flex flex-col space-y-2 ml-4'>
                            <Button
                              onClick={() => startEditComment(comment)}
                              variant='outline'
                              size='sm'
                              className='rounded-full text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200'
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => deleteComment(comment.id)}
                              variant='destructive'
                              size='sm'
                              className='rounded-full text-xs bg-red-500 hover:bg-red-600'
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Password Reset Modal */}
      {resetPasswordUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Reset Password</h3>
            <p className="text-gray-600 mb-6">
              Reset password for <span className="font-semibold">{resetPasswordUser.name}</span>
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password (min 6 characters)
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter new password"
                minLength={6}
              />
            </div>
            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                ⚠️ This will log out the user from all devices. They will need to log in again with the new password.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => {
                  setResetPasswordUser(null);
                  setNewPassword('');
                }}
                variant="outline"
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetPassword}
                className="px-6 bg-purple-600 hover:bg-purple-700 text-white"
              >
                Reset Password
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
